import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Button from 'react-bootstrap/Button'
import { Modal } from 'react-bootstrap'
import axios from '../axiosInstance'
import Sidebar from '../components/sidebar'
import Topbar from '../components/topbar'
import { errorMessage } from '../hooks/UserLogs'
import { formatQueryDate, getToday } from '../utils/date'
import { calcSuccessRate, formatMilliseconds } from '../utils/format'
import './ApiGroup.css'

export default function ApiGroup () {
  const lng = useSelector((state) => state)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const bUrl = process.env.REACT_APP_B_URL

  const dtFromUrl = searchParams.get('dt')
  const [selectedDate, setSelectedDate] = useState(() => {
    if (dtFromUrl) {
      const parsed = new Date(dtFromUrl)
      if (!Number.isNaN(parsed.getTime())) {
        return parsed
      }
    }
    return getToday()
  })
  const [apiGroups, setApiGroups] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [showError, setShowError] = useState(false)
  const [errMsg, setErrMsg] = useState('')

  const axiosInstance = axios.create({ withCredentials: true })

  const fetchApiGroup = useCallback(
    async (date) => {
      setIsLoading(true)
      const dt = formatQueryDate(date)
      let url = bUrl + '/api-group?dt=' + encodeURIComponent(dt)

      const response = await axiosInstance.get(url).catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          setErrMsg(lng.UnauthorizedOperation || "Vous n'êtes pas autorisé à faire cette opération.")
          setShowError(true)
          if (err?.response?.status === 401) {
            navigate('/backoffice/login')
          }
        } else {
          setErrMsg(lng.GenericError || "Une erreur s'est produite.")
          setShowError(true)
        }
        setIsLoading(false)
        return null
      })

      if (!response) return

      if (response.status === 401) {
        navigate('/backoffice/login')
        return
      }

      if (response?.data?.isError) {
        const msg = errorMessage(response?.data?.code) || response?.data?.msg || lng.GenericError
        setErrMsg(msg)
        setShowError(true)
        setApiGroups([])
      } else {
        setApiGroups(response.data.body || [])
      }
      setIsLoading(false)
    },
    [bUrl, lng.GenericError, lng.UnauthorizedOperation, navigate]
  )

  const handleDateChange = (value) => {
    setSelectedDate(new Date(value))
  }

  const handleResetDate = () => {
    setSelectedDate(getToday())
  }

  const handleSearch = () => {
    fetchApiGroup(selectedDate)
  }

  const openDetail = (row) => {
    const dt = formatQueryDate(selectedDate)
    const params = new URLSearchParams({ dt })
    if (row.apiName) {
      params.set('apiName', row.apiName)
    }
    navigate('/api-records/' + row.apiType + '?' + params.toString())
  }

  useEffect(() => {
    if (sessionStorage.getItem('name') === '' || sessionStorage.getItem('name') === null) {
      navigate('/backoffice/login')
      return
    }
    fetchApiGroup(selectedDate)
  }, [selectedDate, fetchApiGroup, navigate])

  return (
    <div className="App list-page api-group-page">
      <Sidebar />
      <section className="home">
        <div className="row">
          <div className="d-grid col-12 gap-3">
            <div className="table-content mt-2 p-2 text d-flex justify-content-between align-items-start">
              <div className="list-page-header">
                <div className="fw-bold">{lng.ApiGroupTitle}</div>
              </div>
              <Topbar />
            </div>

            <div className="d-grid pb-2 gap-3">
              <div className="list-filter-card">
                <div className="list-filter-toolbar">
                  <div className="list-filter-fields">
                    <div className="list-field">
                      <label htmlFor="api-group-date">{lng.ApiGroupDate}</label>
                      <input
                        id="api-group-date"
                        type="date"
                        value={selectedDate.toLocaleDateString('en-CA')}
                        onChange={(e) => handleDateChange(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="list-actions-group" role="group" aria-label={lng.SearchLogs}>
                    <button type="button" className="list-btn-primary" onClick={handleSearch} disabled={isLoading}>
                      <i className="bx bx-search" />
                      {lng.SearchLogs}
                    </button>
                    <button type="button" className="list-btn-ghost" onClick={handleResetDate} disabled={isLoading}>
                      {lng.ResetFilters}
                    </button>
                  </div>
                </div>
              </div>

              <div className="list-table-wrap p-3">
                {isLoading ? (
                  <div className="loading">
                    <img src="../Custom.svg" alt="" />
                  </div>
                ) : apiGroups.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table list-table text mb-0">
                      <thead>
                        <tr>
                          <th scope="col">#</th>
                          <th scope="col">{lng.ApiName}</th>
                          <th scope="col" className="text-end">{lng.ReqCount}</th>
                          <th scope="col" className="text-end">{lng.SucCount}</th>
                          <th scope="col" className="text-end">{lng.FailCount}</th>
                          <th scope="col" className="text-end">{lng.TimeoutCount}</th>
                          <th scope="col" className="text-end">{lng.SuccessRate}</th>
                          <th scope="col" className="text-end">{lng.AvgResponseTime}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apiGroups.map((row, index) => (
                          <tr
                            key={row.apiType}
                            className="list-row-clickable"
                            onClick={() => openDetail(row)}
                            title={lng.ViewApiRecords}
                          >
                            <td>{index + 1}</td>
                            <td className="list-cell-mono">{row.apiName}</td>
                            <td className="text-end">{row.reqCount}</td>
                            <td className="text-end">{row.sucCount}</td>
                            <td className="text-end">{row.failCount}</td>
                            <td className="text-end">{row.timeoutCount}</td>
                            <td className="text-end">{calcSuccessRate(row.sucCount, row.reqCount)}</td>
                            <td className="text-end">{formatMilliseconds(row.aveSec)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="list-empty">
                    <i className="bx bx-bar-chart-alt-2" />
                    <p>{lng.NoApiGroupData}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Modal show={showError} onHide={() => setShowError(false)}>
        <Modal.Header closeButton className="bg-danger">
          <Modal.Title>{lng.ErrorTitle || 'ERROR'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5 className="text-danger text-center">{errMsg}</h5>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowError(false)}>
            {lng.Close}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
